import { useMemo, useRef, useState } from "react";
import { UI } from "../constants/tokens";
import { computeOverlap } from "../utils/geoEvents";
import { quotedHeadline } from "../utils/voice";
import LTR from "./LTR";
import SourceQuote from "./SourceQuote";
import OfficialBadge from "./OfficialBadge";
import PortfolioTie from "./PortfolioTie";
import CardKind from "./CardKind";

// ─── AI insight card ─────────────────────────────────────────────────────────
// The one surface in the app that wears gold, per VISION.md: gold marks an AI
// insight, a Pro feature or the primary action — nothing else.
//
// Every insight here is a row that already exists in geo_events: classified
// once by the geoclassify function, cached, and read by HomeTab. This card
// makes no API call of its own and invents nothing — a row without a Hebrew
// summary or without a source name is not shown at all, and when no row
// qualifies the card does not render.
//
// Content follows the four-question template. The card answers three:
//   1. What changed?          → the event headline, as the source wrote it
//   2. Why is it relevant?    → deterministic portfolio overlap, client-side
//   3. What is it based on?   → source name + when it was published
// The fourth ("what to keep watching") lives in the full intelligence view,
// which is what the button opens.

const MAX_INSIGHTS = 3;
const SWIPE_COMMIT = 40;   // px of travel before the card changes

// ─── Nothing decorative here any more ───────────────────────────────────────
// A gold spark-line used to sit above the relevance line: a curve derived from
// no series, carrying no axis and no scale, there for atmosphere. VISION.md
// carried an explicit exception for it — the one place gold was allowed to be
// decoration. David removed it on 2026-09-23 after seeing it on a phone, and
// the exception went with it: gold now means AI insight, Pro, or the one
// primary action, with no carve-out.
//
// If a real series ever becomes available here, it arrives as a labelled
// chart with an axis — not as a shape that looks like one.

function dayWord(iso) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const days = Math.floor((Date.now() - t) / 86_400_000);
  if (days <= 0) return "היום";
  if (days === 1) return "אתמול";
  return `לפני ${days} ימים`;
}

// "Why this matters to you" — straight from the deterministic overlap, never
// from the model. Returns null when there is nothing true to say.
//
// The two levels must not be confusable. A `hot` row names a company the user
// actually holds; a `geo` row names a company the user does NOT hold, and is
// relevant only because something else in the portfolio shares its sector.
// Wording them both as "בתיק שלך" told the user they owned Broadcom when they
// owned a different chip stock, so `geo` now says which of their symbols
// carries the sector and which company the article is really about.
function relevanceLine(overlap) {
  if (overlap.level === "hot") {
    const held = overlap.heldSymbols;
    const watched = overlap.watchedSymbols;
    return (
      <>
        {held.length > 0 && (
          <PortfolioTie symbols={held} weight={overlap.weightPct > 0 ? overlap.weightPct : null}/>
        )}
        {watched.length > 0 && (
          <PortfolioTie symbols={watched} held={false} style={{ marginTop: held.length ? 3 : 0 }}/>
        )}
      </>
    );
  }
  if (overlap.level === "geo") {
    const mine = overlap.sectorSymbols;
    const subject = overlap.otherTickers[0];
    return (
      <>
        {mine.length > 0 && <PortfolioTie symbols={mine} relation="sector"/>}
        {subject && (
          <div style={{ marginTop: 3 }}>
            הכתבה עוסקת ב־<LTR>{subject}</LTR>, שאינה בתיק שלך
          </div>
        )}
      </>
    );
  }
  return null;
}

export default function InsightCard({ geoEvents, portfolio = [], watchlist = [], onOpen, style }) {
  const [idx, setIdx] = useState(0);
  const [drag, setDrag] = useState(0);
  const touch = useRef(null);

  // Only rows that can answer all three questions truthfully qualify.
  const insights = useMemo(() => {
    if (!Array.isArray(geoEvents)) return [];
    const out = [];
    for (const ev of geoEvents) {
      if (!ev.category || ev.category === "none") continue;
      // The headline is rendered as an attributed quotation, so it is shown as
      // published — Hebrew when the row has it. It is not run through the
      // advisory gate: see quotedHeadline. Everything Suricata writes on this
      // card is still its own voice and still guarded.
      const quote = quotedHeadline(ev);
      const source = typeof ev.source_name === "string" ? ev.source_name.trim() : "";
      // No headline, no summary or no named source → we cannot say where this
      // came from, so it is not an insight. A named source is what makes the
      // quotation attributable, which is what earns it its place at all.
      if (!quote || !ev.summary_he || !source) continue;
      const overlap = computeOverlap(ev, portfolio, watchlist);
      const why = relevanceLine(overlap);
      if (!why) continue;                       // no real link to this portfolio
      out.push({ ev, quote, source, why });
      if (out.length === MAX_INSIGHTS) break;
    }
    return out;
  }, [geoEvents, portfolio, watchlist]);

  // Scope tag: distinct named sources behind the insights on screen, and how
  // recent the newest of them is. Both counted, neither estimated.
  const tag = useMemo(() => {
    if (insights.length === 0) return null;
    const names = new Set(insights.map(i => i.source));
    const newest = insights
      .map(i => i.ev.published_at || i.ev.created_at)
      .sort()
      .pop();
    const when = dayWord(newest);
    const what = names.size === 1 ? "מקור אחד" : `${names.size} מקורות`;
    return when ? `${what} · ${when}` : what;
  }, [insights]);

  // Nothing true to show → no card at all. Never an empty gold frame.
  if (insights.length === 0) return null;

  const active = Math.min(idx, insights.length - 1);
  const multi = insights.length > 1;

  // RTL carousel: the next card sits to the left, so a finger moving right
  // pulls it in. Same direction as the tab carousel (fixed product decision).
  const onTouchStart = (e) => {
    if (!multi) return;
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, locked: null };
  };
  const onTouchMove = (e) => {
    const st = touch.current;
    if (!st) return;
    const dx = e.touches[0].clientX - st.x;
    const dy = e.touches[0].clientY - st.y;
    if (st.locked === null) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 12) return;
      st.locked = Math.abs(dx) > Math.abs(dy) * 1.2;
    }
    if (!st.locked) return;
    // Resist at the ends so the track cannot be dragged into empty space.
    const atStart = active === 0 && dx < 0;
    const atEnd   = active === insights.length - 1 && dx > 0;
    setDrag(atStart || atEnd ? dx * 0.25 : dx);
  };
  const onTouchEnd = () => {
    const st = touch.current;
    touch.current = null;
    if (st?.locked && Math.abs(drag) > SWIPE_COMMIT) {
      setIdx(drag > 0
        ? Math.min(active + 1, insights.length - 1)
        : Math.max(active - 1, 0));
    }
    setDrag(0);
  };

  // Nothing but headlines and sources → the card collapses to card size.
  const compact = insights.every(i => !i.why);

  return (
    <div
      data-noswipe                      /* the tab carousel must not also move */
      className="si-card-press"
      // Same target as the button below: the card the user is looking at. The
      // body used to call onOpen with no argument, which opened the list — two
      // outcomes from one card, depending on where the finger landed.
      onClick={() => onOpen?.(insights[active]?.ev?.source_url || null)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        borderRadius: 18,
        // Roomier than the other cards on purpose — but only when it has
        // something to be roomy about.
        padding: compact ? "16px 18px 14px" : "18px 20px 16px",
        marginBottom: 4,
        // Depth in layers, not in objects: the card sits one surface step above
        // its neighbours and the lift comes from a warm glow, not from a box.
        // Both the glow and the shadow are per-theme tokens — on ivory the
        // night values would read as a stain, so day carries its own pair at a
        // fraction of the strength.
        // A card, not a slab. The screen now has one anchor (the hero on the
        // home tab) and this must not compete with it: the gold hairline and
        // the faint glow keep the insight identity, while the surface and the
        // lift are the ordinary card's, so the eye lands on the anchor first.
        background: `var(--si-insight-glow), ${UI.surface.raised}`,
        border: `1px solid ${UI.accent.hairline}`,
        boxShadow: UI.shadow.card,
        ...style,
      }}
    >
      {/* Identity row + scope tag */}
      <CardKind kind="ai" gold style={{ marginBottom: 12 }} trailing={tag && (
        <span style={{
          fontSize: 11, fontWeight: 700, color: UI.text.muted,
          border: `1px solid ${UI.line.hairline}`, borderRadius: 999, padding: "2px 10px",
          whiteSpace: "nowrap",
        }}>
          {tag}
        </span>
      )}/>

      {/* Viewport — clips the track inside the padding box, otherwise the
          neighbouring slide shows through the card's own padding. */}
      <div style={{ overflow: "hidden" }}>
      <div style={{
        display: "flex",
        transform: `translateX(calc(${active * 100}% + ${drag}px))`,
        transition: drag ? "none" : "transform .28s cubic-bezier(.22,.61,.36,1)",
      }}>
        {insights.map(({ ev, quote, source, why }) => (
          <div key={ev.source_url || ev.id} style={{ flex: "0 0 100%", minWidth: 0, paddingInlineEnd: 2 }}>
            {/* 1 · what changed — and 3 · what it is based on, together.
                The source belongs to the quotation, so it sits with it rather
                than at the bottom of the card. */}
            {/* A Federal Register document is the event itself; the mark says
                so before the quotation, and renders nothing for an article. */}
            <OfficialBadge ev={ev}/>
            <SourceQuote quote={quote} source={source} when={dayWord(ev.published_at || ev.created_at)} clampLines={2}/>

            {/* 2 · why it is relevant to this portfolio. A hairline above it
                separates the quotation from our own line about it, and it is
                drawn only when there is something to draw: a card holding
                nothing but a headline and a source must not be the tallest
                thing on the screen saying the least. */}
            {why ? (
              <div style={{ fontSize: 15, color: UI.text.secondary, lineHeight: 1.6, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${UI.line.hairline}` }}>
                {why}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      </div>

      {/* Primary action — the fourth question is answered inside */}
      <button
        // The carousel can be on any of the insights, so the button hands back
        // which one — otherwise the caller can only open the list and the user
        // has to find the row they just tapped.
        onClick={(e) => { e.stopPropagation(); onOpen?.(insights[active]?.ev?.source_url || null); }}
        style={{
          marginTop: 12,
          background: "transparent",
          border: `1px solid ${UI.accent.base}`,
          borderRadius: 10,
          padding: "10px 18px",
          minHeight: 44,
          fontSize: 14,
          fontWeight: 700,
          color: UI.accent.base,
          cursor: "pointer",
          fontFamily: "inherit",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}>
        לפרטים
        <span aria-hidden style={{ fontSize: 15, lineHeight: 1 }}>‹</span>
      </button>

      {/* Dots — only when there is more than one insight */}
      {multi && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 12 }}>
          {insights.map((_, i) => (
            // The tap target stays 44px tall for touch; only the painted pill
            // inside it is 6px. index.css forces min-height:44px on buttons,
            // which is why the dot itself must not be the button's own box.
            <button
              key={i}
              aria-label={`כותרת ${i + 1}`}
              onClick={(e) => { e.stopPropagation(); setIdx(i); }}
              style={{
                background: "transparent", border: "none", padding: "0 3px",
                height: 20, minHeight: 20, cursor: "pointer",
                display: "flex", alignItems: "center",
              }}>
              <span style={{
                display: "block",
                width: i === active ? 18 : 6, height: 6, borderRadius: 999,
                background: i === active ? UI.text.secondary : UI.line.strong,
                transition: "width .25s ease, background .25s ease",
              }}/>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
